import { useEffect, useRef, useState, useCallback } from 'react';
import VideoScrubber from '@/components/special-effects/lighting-river/components/VideoScrubber';
import HeaderNav from '@/components/special-effects/lighting-river/scenes/HeaderNav';
import HeroScene from '@/components/special-effects/lighting-river/scenes/HeroScene';
import BrandScene from '@/components/special-effects/lighting-river/scenes/BrandScene';
import DirectionsScene from '@/components/special-effects/lighting-river/scenes/DirectionsScene';
import BooksScene from '@/components/special-effects/lighting-river/scenes/BooksScene';
import InfoScene from '@/components/special-effects/lighting-river/scenes/InfoScene';
import BlogScene from '@/components/special-effects/lighting-river/scenes/BlogScene';
import CTAScene from '@/components/special-effects/lighting-river/scenes/CTAScene';
import { sceneProgress } from '@/components/special-effects/lighting-river/scenes/progress';

const MAX_SCROLL = 3.25;

// 每個場景在全域進度中的起訖位置
const SCENE_WINDOWS = {
  hero: { start: 0.0, end: 0.14 },
  brand: { start: 0.14, end: 0.52 },
  directions: { start: 0.52, end: 0.98 },
  books: { start: 0.98, end: 1.6 },
  info: { start: 1.6, end: 2.25 },
  blog: { start: 2.25, end: 2.85 },
  cta: { start: 2.85, end: 3.2 },
} as const;

// 導覽按鈕跳轉到的目標進度
// 注意：每個場景在「起點」時 opacity 還炋 0（進場動畫剛開始），
// 所以跳轉落點必須往場景內部推，否則會落在兩個場景的交界處而看到空白畫面。
function sceneAnchor(start: number, end: number): number {
  return start + (end - start) * 0.45;
}

const SCENE_ANCHORS: Record<string, number> = {
  hero: 0,
  brand: sceneAnchor(SCENE_WINDOWS.brand.start, SCENE_WINDOWS.brand.end),
  directions: sceneAnchor(SCENE_WINDOWS.directions.start, SCENE_WINDOWS.directions.end),
  books: sceneAnchor(SCENE_WINDOWS.books.start, SCENE_WINDOWS.books.end),
  info: sceneAnchor(SCENE_WINDOWS.info.start, SCENE_WINDOWS.info.end),
  blog: sceneAnchor(SCENE_WINDOWS.blog.start, SCENE_WINDOWS.blog.end),
  channels: sceneAnchor(SCENE_WINDOWS.cta.start, SCENE_WINDOWS.cta.end),
};

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

function deriveSection(p: number): string {
  if (p < SCENE_WINDOWS.brand.start) return 'hero';
  if (p < SCENE_WINDOWS.directions.start) return 'brand';
  if (p < SCENE_WINDOWS.books.start) return 'directions';
  if (p < SCENE_WINDOWS.info.start) return 'books';
  if (p < SCENE_WINDOWS.blog.start) return 'info';
  if (p < SCENE_WINDOWS.cta.start) return 'blog';
  return 'channels';
}

export default function Home() {
  const scrollTargetRef = useRef<number>(0);
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  const lerpedRef = useRef<number>(0);
  const [lerped, setLerped] = useState<number>(0);

  const [activeSectionId, setActiveSectionId] = useState<string>('hero');

  const navAnimRef = useRef<number | null>(null);
  const navCancelRef = useRef<boolean>(false);

  const cancelProgrammaticNav = useCallback(() => {
    if (navAnimRef.current !== null) {
      cancelAnimationFrame(navAnimRef.current);
      navAnimRef.current = null;
      navCancelRef.current = true;
    }
  }, []);

  // 手勢控制器：滾輪 / 觸控驅動進度
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    let lastTouchY = 0;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      cancelProgrammaticNav();
      const next = clamp(scrollTargetRef.current + e.deltaY * 0.0006, 0, MAX_SCROLL);
      scrollTargetRef.current = next;
      setScrollProgress(next);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) lastTouchY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      e.preventDefault();
      cancelProgrammaticNav();
      const currentY = e.touches[0].clientY;
      const delta = lastTouchY - currentY;
      lastTouchY = currentY;
      const next = clamp(scrollTargetRef.current + delta * 0.0015, 0, MAX_SCROLL);
      scrollTargetRef.current = next;
      setScrollProgress(next);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      // 離開首頁時恢復正常捲動，避免其他頁面被鎖死
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [cancelProgrammaticNav]);

  // RAF 平滑 + 場景偵測
  useEffect(() => {
    let raf: number;
    const loop = () => {
      const target = scrollTargetRef.current;
      const cur = lerpedRef.current;
      const diff = target - cur;
      if (Math.abs(diff) > 0.0001) {
        lerpedRef.current = cur + diff * 0.08;
        setLerped(lerpedRef.current);
        const section = deriveSection(lerpedRef.current);
        setActiveSectionId((prev) => (prev === section ? prev : section));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // 程式化跳轉
  const handleNavigateToSection = useCallback(
    (targetScrollRatio: number) => {
      cancelProgrammaticNav();
      navCancelRef.current = false;

      const startVal = scrollTargetRef.current;
      const endVal = clamp(targetScrollRatio, 0, MAX_SCROLL);
      const startTime = performance.now();
      const duration = 1200;

      const easeInOutCubic = (p: number) =>
        p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

      const step = (now: number) => {
        if (navCancelRef.current) return;
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = easeInOutCubic(t);
        const v = startVal + (endVal - startVal) * eased;
        scrollTargetRef.current = v;
        setScrollProgress(v);
        if (t < 1) {
          navAnimRef.current = requestAnimationFrame(step);
        } else {
          navAnimRef.current = null;
        }
      };

      navAnimRef.current = requestAnimationFrame(step);
    },
    [cancelProgrammaticNav]
  );

  // 依 section key 跳轉
  const navigateToKey = useCallback(
    (sectionKey: string) => {
      const target = SCENE_ANCHORS[sectionKey];
      if (target !== undefined) handleNavigateToSection(target);
    },
    [handleNavigateToSection]
  );

  return (
    <main
      className="relative w-screen h-screen overflow-hidden text-white"
      style={{ backgroundColor: '#0A1A3A' }}
    >
      <div className="relative w-full h-full overflow-hidden">
        {/* 影片剪輯背景 */}
        <VideoScrubber scrollProgress={Math.min(1, lerped)} />

        {/* 深色覆蓋層，確保文字可讀 */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(7,18,48,0.55) 0%, rgba(7,18,48,0.75) 55%, rgba(7,18,48,0.9) 100%)' }}
        />

        {/* 各場景 */}
        <HeroScene
          progress={sceneProgress(lerped, SCENE_WINDOWS.hero.start, SCENE_WINDOWS.hero.end)}
          navigateTo={handleNavigateToSection}
          brandAnchor={SCENE_ANCHORS.brand}
          booksAnchor={SCENE_ANCHORS.books}
        />
        <BrandScene progress={sceneProgress(lerped, SCENE_WINDOWS.brand.start, SCENE_WINDOWS.brand.end)} />
        <DirectionsScene progress={sceneProgress(lerped, SCENE_WINDOWS.directions.start, SCENE_WINDOWS.directions.end)} />
        <BooksScene progress={sceneProgress(lerped, SCENE_WINDOWS.books.start, SCENE_WINDOWS.books.end)} />
        <InfoScene progress={sceneProgress(lerped, SCENE_WINDOWS.info.start, SCENE_WINDOWS.info.end)} />
        <BlogScene progress={sceneProgress(lerped, SCENE_WINDOWS.blog.start, SCENE_WINDOWS.blog.end)} />
        <CTAScene progress={sceneProgress(lerped, SCENE_WINDOWS.cta.start, SCENE_WINDOWS.cta.end)} />

        {/* 導覽 */}
        <HeaderNav activeSection={activeSectionId} navigateTo={navigateToKey} />
      </div>
    </main>
  );
}
